import { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useUser from 'src/hooks/use-user';
import { useExclusiveMenuGateModal } from 'src/components/modals/exclusive-menu-gate';

type UseExclusiveCollectionGateProps = {
  isExclusive?: boolean;
  isAccessibleToCurrentUser?: boolean;
  loading: boolean;
};

/**
 * Hook to show the exclusive collection gate modal when appropriate.
 * Opens the modal if the collection is exclusive and the user either:
 * - Is not logged in, OR
 * - Is logged in but doesn't have access
 */
export function useExclusiveCollectionGate({
  isExclusive,
  isAccessibleToCurrentUser,
  loading,
}: UseExclusiveCollectionGateProps): void {
  const { isLoggedIn } = useUser();
  const { open } = useExclusiveMenuGateModal();

  const flags = useFlags();
  const isExclusiveCollectionsEnabled = flags['ecommerce.exclusive-collections.rollout'] ?? false;

  useEffect(() => {
    if (loading || !isExclusive || !isExclusiveCollectionsEnabled) {
      return;
    }

    if (isAccessibleToCurrentUser === undefined || (isLoggedIn && isAccessibleToCurrentUser)) {
      return;
    }

    void open({
      isAccessibleToCurrentUser,
      isLoggedIn,
    });
  }, [loading, isExclusive, isLoggedIn, isAccessibleToCurrentUser, open, isExclusiveCollectionsEnabled]);
}
